// The site home for a visitor with no session (SITE_HOME.md, ACCOUNTS.md,
// scenarios A1 and A2): what this is, a picture of it, and the doors a
// newcomer came for and nothing else (DIRECTION A2, B-305). Start and Join
// open the sign-up sheet, which crosses over to signing in; the sample league
// needs no account; the corner signs in or makes an account on its own. It
// looks like the app, not a landing page. Drawn through the kit (LOOKS.md
// Part 7).

import { useState } from 'react';
import { AboutButton } from '../shell/AboutButton';
import { Link, useNavigate } from 'react-router-dom';
import { BRAND } from '../branding';
import { DEMO_PATH } from '../data/demo';
import { useTier } from '../shell/useTier';
import { listSports } from '../sports/registry';
import { SignUpSheet, type SignUpMode, type SignUpReason } from './shared/SignUpSheet';
import { Button, Text } from '../kit/ui';
import { WelcomePicture } from './WelcomePicture';
import { DataCredit } from './shared/DataCredit';
import '../styles/shell.css';

export const START_REASON: SignUpReason = {
  needs: 'Creating a league needs an account first.',
  then: 'A league has to belong to someone, and that is you — it is where the rules, the members and the invite link live. A name, an email and a password, then a few questions about your league and a link to send your friends.',
};

export const JOIN_REASON: SignUpReason = {
  needs: 'Joining a league needs an account first.',
  then: "The league has to know who you are, and your team has to belong to someone. After this you'll paste the code and name your team.",
};

/** The sports from the registry, as a phrase: "Football", "Football and Hockey". */
function sportsPhrase(): string {
  const names = listSports().map((s) => s.name);
  return names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export function WelcomeScreen({
  initialSheet = null,
  next: nextProp,
  reason: reasonProp,
}: {
  /** Open a sheet right away, as when a guest follows an invite link. */
  initialSheet?: SignUpMode | null;
  /** Where to land after signing up; the door the visitor chose sets it otherwise. */
  next?: string;
  /**
   * Why the sheet is open, when it was opened FOR the visitor rather than by
   * them — following an invite link, or asking for a page that needs an
   * account.
   *
   * Without this the sheet arriving on its own was a bare form with no
   * explanation: tapping Start a league threw up a sign-up popup that said
   * nothing about why, which only confuses.
   */
  reason?: SignUpReason;
}) {
  const tier = useTier();
  const navigate = useNavigate();
  const [sheet, setSheet] = useState<SignUpMode | null>(initialSheet);
  const [next, setNext] = useState(nextProp ?? '/');
  // The reason the visitor was GIVEN, when they opened a door themselves.
  // Null means "whatever the caller says", which is the invite case.
  const [chosen, setChosen] = useState<SignUpReason | null>(null);
  /**
   * WHY THIS IS NOT `useState(reasonProp)`, which is what it was.
   *
   * `useState` reads its argument on the FIRST render and never again, so a
   * caller that learns the reason late — `JoinGate`, which asks the server
   * what an invite code names — was ignored. And whether it was ignored was a
   * RACE: if the preview answered before the session reconciled, the sheet had
   * not mounted yet and the league name arrived in the initial value; if the
   * session won, the sheet mounted with the generic sentence and kept it
   * forever. Two headless walks minutes apart disagreed about whether the
   * feature worked, which is how it was found.
   *
   * Same shape as every other bug found on 2026-09-15: something that decides
   * once, before it has the answer, and cannot reconsider.
   */
  const reason = chosen ?? reasonProp;
  const phone = tier === 'phone';

  const openDoor = (mode: SignUpMode, to: string, why?: SignUpReason) => {
    setNext(nextProp ?? to);
    setChosen(why ?? null);
    setSheet(mode);
  };

  const done = () => {
    setSheet(null);
    navigate(next);
  };

  const doors = (
    <Doors
      size={phone ? 'md' : 'lg'}
      onStart={() => openDoor('sign-up', '/leagues/new', START_REASON)}
      onJoin={() => openDoor('sign-up', '/join', JOIN_REASON)}
      onSample={() => navigate(DEMO_PATH)}
    />
  );

  return (
    <div className="fsp-frame fsp-welcome" data-tier={tier}>
      <header className="fsp-topbar">
        <div className="fsp-topbar-left">
          <span className="fsp-brand fsp-disp">{BRAND.shortName}</span>
        </div>
        <div className="fsp-topbar-right">
          {/* Which build this is, before anybody signs in too (O2, B-112). */}
          <AboutButton />
          <Button variant="quiet" size="xs" onClick={() => openDoor('sign-in', '/')}>
            Sign in
          </Button>
          {/* BOTH DOORS IN THE CORNER: somebody new looks where sign-in is
              for where to start (B-231). */}
          <Button variant="primary" size="xs" onClick={() => openDoor('sign-up', '/')}>
            Create account
          </Button>
        </div>
      </header>

      <main className="fsp-content">
        <div className="fsp-welcome-scroll">
          {/* WHAT THIS IS, A PICTURE OF IT, AND THE DOORS (B-305). Five
              feature tiles sat here until 2026-10-12 — Start a league, Draft
              live, Play the week, Talk trash, Make it yours — each opening an
              explainer: more things to choose between for somebody who has not
              chosen anything yet, led by the competitive side of a site meant
              to feel at home to a family (DIRECTION P1). The picture shows a
              week instead, and the sample league shows the rest, working. */}
          <div className="fsp-welcome-main">
            <div className="fsp-welcome-intro">
              <div className="fsp-welcome-hero">
                <div className="fsp-disp fsp-welcome-title">{BRAND.name}</div>
                <div className="fsp-welcome-tagline">{BRAND.tagline}</div>
                <Text tone="secondary" block>
                  {sportsPhrase()} leagues. Free, with no ads and no betting.
                </Text>
              </div>
              {phone ? null : doors}
            </div>
            <WelcomePicture full={!phone} />
          </div>
          {/* THE FOOT (B-417): what the site keeps, before anybody types an
              email, where its numbers come from (B-593), and which build
              this is. */}
          <footer className="fsp-welcome-foot fsp-sub">
            <DataCredit link={false} />
            <div>
              <Link className="fsp-linklike" to="/privacy">
                Privacy
              </Link>
              <span aria-hidden="true"> · </span>
              <Link className="fsp-linklike" to="/credits">
                Credits
              </Link>
              <span aria-hidden="true"> · </span>
              <Link className="fsp-linklike" to="/about">
                About this build
              </Link>
            </div>
          </footer>
        </div>
        {/* A phone's doors sit at the bottom, where a thumb is. */}
        {phone ? <div className="fsp-welcome-bar">{doors}</div> : null}
      </main>

      {sheet ? (
        <SignUpSheet
          mode={sheet}
          opened
          onClose={() => setSheet(null)}
          onDone={done}
          reason={reason}
        />
      ) : null}
    </div>
  );
}

function Doors({
  size,
  onStart,
  onJoin,
  onSample,
}: {
  size: 'md' | 'lg';
  onStart: () => void;
  onJoin: () => void;
  onSample: () => void;
}) {
  return (
    // The hint is a sibling of the button row, not a child of it. Inside,
    // it became a fourth flex item on a row that does not wrap: the buttons
    // shrank until their labels clipped ("Start a lea") and the hint sat off
    // to the right instead of under them.
    <div className="fsp-welcome-actions-wrap">
      <div className="fsp-welcome-actions">
        <Button size={size} variant="primary" icon="add" onClick={onStart}>
          Start a league
        </Button>
        {/* "Join a league", as the screen it opens is titled: a newcomer may
            hold a link rather than a code. */}
        <Button size={size} icon="ticket" onClick={onJoin}>
          Join a league
        </Button>
        <Button size={size} variant="soft" icon="play" onClick={onSample}>
          See a sample league
        </Button>
      </div>
      {/* Says what happens before the visitor commits: two doors ask for an
          account, the third asks for nothing. */}
      <div className="fsp-sub fsp-ui-wrap-text fsp-welcome-hint">
        Starting or joining a league needs an account. The sample league doesn&rsquo;t.
      </div>
    </div>
  );
}
